import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { debounce, isDefined } from '../helpers/utils';

import { PointCardPagination } from './point-card-pagination';
import { detectMobileViewport } from '../helpers/device';
import topicData from '../helpers/state/topic-data';

@customElement('point-drawer')
export class PointDrawer extends LitElement {
    @query('#point-drawer__content')
    content?: HTMLDivElement;

    @property()
    pointCardPagination!: PointCardPagination;

    @state()
    isOpened: boolean = false;

    @state()
    mobilePreview: boolean = false;

    @property({ type: Number })
    bottomDrawerHeight: number = 0;

    close() {
        this.isOpened = false;

        this.dispatchEvent(new CustomEvent('on-close'));
    }

    scrollToBeginning() {
        if (isDefined(this.content)) {
            if (detectMobileViewport(this.mobilePreview)) {
                this.content.scroll({left: 0, behavior:'smooth'});
            } else {
                this.content.scroll({ top: 0, behavior:'smooth' });
            }
        }
    }

    open() {
        this.isOpened = true;
    }

    // Logic for lazy loading of point cards. this is only for vertical scroll
    // Horizontal scroll handling is in point-card-pagination
    connectedCallback() {
        super.connectedCallback();
        setTimeout(() => {
            this.content?.addEventListener('scroll', debounce(this.checkScrollPosition, 200));
        }, 0);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        setTimeout(() => {
            this.content?.removeEventListener('scroll', debounce(this.checkScrollPosition, 200));
        }, 0);
    }

    checkScrollPosition = () => {
        if (topicData.mapComponent?.pointCardPaginationEl.isInitialized !== true || topicData.mapComponent?.pointCardPaginationEl.isProgrammaticallyScrolling || !topicData.mapComponent?.pointCardPaginationEl.useInfiniteScroll) return;
        const scrollableArea = this.content;
        if (!scrollableArea) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollableArea;
        const endOfScrollableArea = scrollTop + clientHeight;
        const beginningOfScrollableArea = scrollTop;
        if (endOfScrollableArea >= scrollHeight - 10 && !topicData.mapComponent?.multiSelectionEnabled) {
            if (topicData.mapComponent?.pointCardPaginationEl.isLastPointLoaded) return;
            void topicData.mapComponent?.pointCardPaginationEl.fetchMorePoints('next');
        } else if (beginningOfScrollableArea <= 10 && !topicData.mapComponent?.multiSelectionEnabled) {
            if (topicData.mapComponent?.pointCardPaginationEl.isFirstPointLoaded) return;
            void topicData.mapComponent?.pointCardPaginationEl.fetchMorePoints('previous');
        }
    };

    render() {
        const bottomOffset = detectMobileViewport(this.mobilePreview) && this.bottomDrawerHeight > 0 
            ? `bottom: ${this.bottomDrawerHeight}px;` 
            : 'bottom: 0;';
        
        return html`
            <span class=${detectMobileViewport(this.mobilePreview) ? 'point-drawer--horizontal' : 'point-card--vertical'}>
                <div id="point-drawer" class="point-drawer ${this.isOpened ? 'point-drawer--opened' : 'point-drawer--closed'}" style="${bottomOffset}">
                    <div id="point-drawer__content" class="point-drawer__content">
                        <slot style="pointer-events: all;"></slot>
                    </div>
                </div>
            </span>
        `;
    }

    static get styles() {
        return css`
            .point-drawer--horizontal .point-drawer {
                z-index: 2;
            }

            .point-drawer--horizontal .point-drawer--opened {
                position: absolute;
                width: 100%;
                bottom: 0;
                align-items: self-end;
                display: flex;
            }

            .point-drawer--horizontal .point-drawer--closed {
                display: none;
            }

            .point-drawer--horizontal .point-drawer__content {
                touch-action: none;
                border-radius: 5px;
                height: calc(100% - 10px);
                margin: 5px;
                overflow-x: scroll;
                overflow-y: hidden;
            }


            .point-card--vertical .point-drawer__content {
                border-radius: 5px;
                overflow: scroll auto;
                height: 100%;
            }

            .point-card--vertical .point-drawer__content::-webkit-scrollbar {
                width: 1px; // this makes the scrollbar virtually invisible
            }

            .point-card--vertical .point-drawer {
                position: absolute;
                z-index: 3;
                transition: 0.3s cubic-bezier(0, 0, 0.2, 1);
                width: min(468px, 35vw);
                top: 0;
                min-width: 350px;
                left: 0;
                height: 100%;
                pointer-events: none;
            }

            .point-card--vertical .point-drawer--opened {
                transform: translateX(0%);
            }

            .point-card--vertical .point-drawer--closed {
                transform: translateX(-100%);
            }

        `;
    }
}
